import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { debug } from '@utils/debug';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser"; // Import from correct package
import {
  LayoutDashboard,
  MessageSquare,
  Settings,
  LogIn,
  LogOut,
  User,
  Upload,
  PanelLeftClose,
  PanelLeft,
  UserCog
} from 'lucide-react';
import PropTypes from 'prop-types';
import { loginRequest } from '@config/auth'; // Import loginRequest

const NavMenu = ({ onMenuVisibilityChange }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0]; // Assuming single account
  const [isMenuVisible, setIsMenuVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Removed authInProgress state, use inProgress from useMsal

  // Debug log when auth state potentially changes
  useEffect(() => {
    debug('nav', 'Auth state update', { account, inProgress });
// Removed old event listener logic
}, [account, inProgress]); // Depend on account and inProgress

  // Notify parent component when menu visibility changes
  useEffect(() => {
    if (onMenuVisibilityChange) {
      onMenuVisibilityChange(isMenuVisible);
    }
  }, [isMenuVisible, onMenuVisibilityChange]);

  // Handle window resize with debounce
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const mobile = window.innerWidth < 768;
        setIsMobile(mobile);
        if (mobile && !document.documentElement.hasAttribute('data-menu-initialized')) {
          setIsMenuVisible(false);
          document.documentElement.setAttribute('data-menu-initialized', 'true');
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Close mobile menu when route changes on mobile
  useEffect(() => {
    if (isMobile) {
      setIsMenuVisible(false);
    }
  }, [location, isMobile]);

  const handleLogin = useCallback(async () => {
     try {
       await instance.loginPopup(loginRequest);
     } catch (error) {
       debug('nav', 'Login failed', { error });
       // Handle specific errors if needed
     }
  }, [instance]);

  const handleLogout = useCallback(async () => {
     try {
       // Consider logoutRedirect if popup causes issues
       await instance.logoutPopup({ account: account });
     } catch (error) {
       debug('nav', 'Logout failed', { error });
     }
  }, [instance, account]);

  const toggleMenu = useCallback(() => {
    setIsMenuVisible(prev => !prev);
  }, []);

  return (
    <>
      {/* Menu toggle button - positioned mostly on the nav menu */}
      <button
        onClick={toggleMenu}
        className={`fixed top-1/2 transform -translate-y-1/2 z-50 p-2 rounded-r-md bg-white shadow-md hover:bg-huntd-light transition-colors
          ${isMenuVisible ? 'left-44' : 'left-0'}`}
        aria-label={isMenuVisible ? "Hide menu" : "Show menu"}
      >
        {isMenuVisible ?
          <PanelLeftClose className="w-6 h-6 text-huntd-info" /> :
          <PanelLeft className="w-6 h-6 text-huntd-accent" />
        }
      </button>

      {/* Overlay when menu is open on mobile */}
      {isMenuVisible && isMobile && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleMenu}
        />
      )}

      <nav className={`bg-white shadow-lg fixed top-0 left-0 z-40 h-screen transition-all duration-300 ease-in-out
        ${isMenuVisible ? 'translate-x-0' : '-translate-x-full'}
        w-52`}
      >
        <div className="flex flex-col justify-between h-full p-4 w-52">
        {/* Top section with logo */}
        <div>
          <Link
            to={account ? "/app/dashboard" : "/"} // Use account presence
            className="flex justify-center mb-12"
          >
            <img src="/huntd_logo.png" alt="Huntd Logo" className="w-full max-w-[200px]" />
          </Link>

          {/* Navigation links */}
          <div className="space-y-2 pt-8">
            <div className="space-y-1">
              <Link
                to="/app/dashboard"
                className={`w-full px-4 py-2 text-base rounded flex items-center gap-2 ${
                  currentPath === '/app/dashboard' ? 'bg-huntd-info text-white' : 'border border-huntd-light hover:bg-huntd-light'
                }`}
              >
                <LayoutDashboard className="w-5 h-5" />
                Dashboard
              </Link>

              <Link
                to="/app/profile"
                className={`w-full px-4 py-2 text-base rounded flex items-center gap-2 ${
                  currentPath === '/app/profile' ? 'bg-huntd-info text-white' : 'border border-huntd-light hover:bg-huntd-light'
                }`}
              >
                <UserCog className="w-5 h-5" />
                Profile
              </Link>
            </div>

            <Link
              to="/app/experience"
              className={`w-full px-4 py-2 text-base rounded flex items-center gap-2 ${
                currentPath === '/app/experience' ? 'bg-huntd-info text-white' : 'border border-huntd-light hover:bg-huntd-light'
              }`}
            >
              <Settings className="w-5 h-5" />
              Experience
            </Link>

            <Link
              to="/app/cv-upload"
              className={`w-full px-4 py-2 text-base rounded flex items-center gap-2 ${
                currentPath === '/app/cv-upload' ? 'bg-huntd-info text-white' : 'border border-huntd-light hover:bg-huntd-light'
              }`}
            >
              <Upload className="w-5 h-5" />
              Upload CV
            </Link>

            <Link
              to="/app/chat"
              className={`w-full px-4 py-2 text-base rounded flex items-center gap-2 ${
                currentPath === '/app/chat' ? 'bg-huntd-info text-white' : 'border border-huntd-light hover:bg-huntd-light'
              }`}
            >
              <MessageSquare className="w-5 h-5" />
              Chat
            </Link>
          </div>
        </div>

        {/* Bottom section with user info/login */}
        <div className="mt-auto">
          {account ? ( // Check if account exists
            <div className="p-2 bg-huntd-light rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4" />
                <span className="truncate">{account.name || account.username || 'User'}</span>
              </div>
              <button
                onClick={handleLogout}
                disabled={inProgress !== InteractionStatus.None} // Disable if MSAL is busy
                className={`mt-2 w-full px-4 py-2 text-sm rounded flex items-center gap-2 bg-huntd-error text-white hover:bg-huntd-error/80 border border-huntd-error ${
                  (inProgress !== InteractionStatus.None) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <LogOut className="w-4 h-4" />
                {inProgress === InteractionStatus.Logout ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogin}
              disabled={inProgress !== InteractionStatus.None} // Disable if MSAL is busy
              className={`w-full px-4 py-2 text-sm rounded flex items-center gap-2 bg-huntd-info text-white hover:bg-huntd-info/80 border border-huntd-info ${
                (inProgress !== InteractionStatus.None) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <LogIn className="w-4 h-4" />
              {inProgress === InteractionStatus.Login ? 'Signing in...' : 'Sign In'}
            </button>
          )}
        </div>
        </div>
      </nav>
    </>
  );
};

NavMenu.propTypes = {
  onMenuVisibilityChange: PropTypes.func
};

NavMenu.defaultProps = {
  onMenuVisibilityChange: () => {}
};

export default NavMenu;
